import { type Matrix4, Vector3 } from 'three';
import type { Pcg32 } from '../../sim/rng';
import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { type BoxColors, MeshBuilder, place } from './MeshBuilder';
import { capColor, snowSlab } from './snow';

const TAU = Math.PI * 2;

/** Concrete bunker: immune to carbine and grenades, needs a LAW or a bomb (02 §9.2). */
export const bunker: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const wall = p.color('concrete.mid');
  mb.box(5.2, 1.5, 4.0, { all: wall, front: p.color('concrete.mid'), top: p.color('concrete.lo') });
  // Overhanging roof slab: the eave catches a shadow line, which is what reads at 16 px/m.
  mb.box(5.9, 0.4, 4.7, { all: p.color('concrete.hi'), top: p.color('concrete.mid') }, place(0, 1.5, 0));
  snowSlab(mb, p, 5.9, 4.7, 1.9);
  // Firing slit and rear door, as dark insets.
  mb.box(3.2, 0.34, 0.12, p.color('ink.0'), place(0, 0.92, 2.0));
  mb.box(0.12, 0.34, 1.8, p.color('ink.0'), place(-2.6, 0.92, 0.6));
  mb.box(0.9, 1.2, 0.12, p.color('ink.1'), place(1.4, 0, -2.0));
  // Buttress blocks break the box silhouette.
  mb.box(0.7, 1.1, 0.7, p.color('concrete.lo'), place(-2.6, 0, 2.0));
  mb.box(0.7, 1.1, 0.7, p.color('concrete.lo'), place(2.6, 0, 2.0));
  return mb;
};

/** Sandbag horseshoe with a machine gun: falls to grenades. Opens to the north (rear). */
export const mgNest: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const radius = 1.7;
  for (let layer = 0; layer < 3; layer++) {
    const count = 9;
    for (let i = 0; i < count; i++) {
      const a = -Math.PI * 0.62 + ((i + (layer % 2) * 0.5) / (count - 1)) * Math.PI * 1.24;
      const x = Math.sin(a) * radius;
      const z = Math.cos(a) * radius;
      const tone = rng.chance(0.5) ? 'canvas.mid' : 'canvas.hi';
      mb.box(0.78, 0.3, 0.42, { all: p.color(tone), top: layer === 2 ? capColor(p, 'canvas.hi') : p.color(tone), front: p.color('canvas.lo') }, place(x, layer * 0.28, z, a));
    }
  }
  // Gun on a tripod, muzzle to the south.
  mb.box(0.1, 0.55, 0.1, p.color('metal.lo'), place(0, 0, 0.6));
  mb.box(0.12, 0.14, 1.25, p.color('metal.lo'), place(0, 0.62, 0.95));
  mb.box(0.3, 0.2, 0.3, p.color('metal.mid'), place(0, 0.58, 0.55));
  return mb;
};

/** Courses of a sandbag run rise by this much; a bag is a little taller, so the top one reaches 0.9 m. */
export const BAG_COURSE = 0.29;
const BAG_HEIGHT = 0.32;

/** One sandbag without its underside, which nobody ever sees: a long run is nothing but bags. */
export function bag(mb: MeshBuilder, w: number, d: number, colors: BoxColors, m: Matrix4): void {
  const x = w / 2;
  const z = d / 2;
  const h = BAG_HEIGHT;
  const v = (px: number, py: number, pz: number): Vector3 => new Vector3(px, py, pz);
  mb.with(m, () => {
    mb.quad(v(-x, h, z), v(x, h, z), v(x, h, -z), v(-x, h, -z), colors.top ?? colors.all);
    mb.quad(v(-x, 0, z), v(x, 0, z), v(x, h, z), v(-x, h, z), colors.front ?? colors.all);
    mb.quad(v(x, 0, -z), v(-x, 0, -z), v(-x, h, -z), v(x, h, -z), colors.back ?? colors.all);
    mb.quad(v(-x, 0, -z), v(-x, 0, z), v(-x, h, z), v(-x, h, -z), colors.left ?? colors.all);
    mb.quad(v(x, 0, z), v(x, 0, -z), v(x, h, -z), v(x, h, z), colors.right ?? colors.all);
  });
}

/**
 * Straight run of sandbags: two and a half courses, 0.9 m to the broken crest - the height of
 * cover the sim gives it (`RUN_HEIGHT.sandbags`), so what hides a crouching man looks as if it does.
 */
export function sandbagRun(p: Palette, rng: Pcg32, length = 4): MeshBuilder {
  const mb = new MeshBuilder();
  const n = Math.round(length / 0.74);
  for (let layer = 0; layer < 3; layer++) {
    for (let i = 0; i < n - layer; i++) {
      // The top course is every other bag: a crest with gaps to fire through.
      if (layer === 2 && i % 2 === 1) continue;
      const x = -length / 2 + 0.37 + i * 0.74 + layer * 0.37;
      const tone = rng.chance(0.5) ? 'canvas.mid' : 'canvas.hi';
      const top = layer > 0 ? capColor(p, 'canvas.hi') : p.color(tone);
      const colors = { all: p.color(tone), top, front: p.color('canvas.lo') };
      bag(mb, 0.72, 0.46, colors, place(x, layer * BAG_COURSE, 0, rng.range(-0.05, 0.05)));
    }
  }
  return mb;
}

/** Timber hut / guard house: enterable interior in the sim, lit window at night. */
export const hut: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const wall = p.biome === 'desert' ? 'concrete' : 'timber';
  mb.box(4.2, 2.3, 3.4, { all: p.color(`${wall}.mid`), front: p.color(`${wall}.mid`), left: p.color(`${wall}.lo`), right: p.color(`${wall}.lo`) });
  if (p.biome === 'desert') {
    // Flat adobe roof with a parapet.
    mb.box(4.6, 0.3, 3.8, { all: p.color('concrete.hi'), top: p.color('concrete.hi') }, place(0, 2.3, 0));
    mb.box(4.6, 0.35, 0.3, p.color('concrete.hi'), place(0, 2.6, 1.75));
    mb.box(4.6, 0.35, 0.3, p.color('concrete.hi'), place(0, 2.6, -1.75));
  } else {
    // Gable with generous eaves; ridge runs east-west so the camera sees the slope.
    mb.gable(5.0, 1.5, 4.4, p.color('roof.1'), place(0, 2.2, 0), p.color(`${wall}.lo`));
    if (p.biome === 'arctic') mb.gable(4.5, 1.12, 3.3, p.color('ground.3'), place(0, 2.66, 0), p.color('ground.2'));
    mb.box(0.5, 1.1, 0.5, p.color('rock.lo'), place(1.2, 2.6, -0.8));
  }
  mb.box(0.95, 1.7, 0.1, p.color('ink.1'), place(-0.9, 0, 1.72));
  mb.box(0.8, 0.7, 0.1, p.color('fire.2'), place(1.0, 1.05, 1.72));
  mb.box(0.92, 0.1, 0.14, p.color(`${wall}.lo`), place(1.0, 1.0, 1.74));
  return mb;
};

/** HQ tent. Ridge runs north-south so the flap faces the camera. */
export const tent: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.with(place(0, 0, 0, Math.PI / 2), () => {
    mb.gable(3.8, 2.0, 3.2, p.color('canvas.mid'), undefined, p.color('canvas.lo'));
    if (p.biome === 'arctic') mb.gable(3.4, 0.9, 1.44, p.color('ground.3'), place(0, 1.16, 0), p.color('ground.2'));
  });
  // Door flap: a dark wedge on the south end.
  mb.gable(0.1, 1.5, 1.1, p.color('ink.1'), place(0, 0, 1.92, Math.PI / 2));
  for (const sx of [-1, 1]) {
    mb.box(0.08, 0.5, 0.08, p.color('timber.lo'), place(sx * 2.0, 0, 1.7));
    mb.box(0.08, 0.5, 0.08, p.color('timber.lo'), place(sx * 2.0, 0, -1.7));
  }
  return mb;
};

/** Watchtower with a roofed platform; carries a searchlight at night. */
export const watchtower: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const leg = p.color('timber.lo');
  const half = 1.1;
  const deckY = 4.2;
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      mb.box(0.2, deckY, 0.2, leg, place(sx * half, 0, sz * half));
    }
  }
  // Cross braces on the two faces the camera can see.
  mb.box(0.12, 2.9, 0.12, leg, place(0, 0.6, half, 0, 1, 0, 0.72));
  mb.box(0.12, 2.9, 0.12, leg, place(0, 0.6, half, 0, 1, 0, -0.72));
  mb.box(2.9, 0.22, 2.9, { all: p.color('timber.mid'), top: p.color('timber.hi') }, place(0, deckY, 0));
  const rail = p.color('timber.mid');
  mb.box(2.9, 0.95, 0.14, rail, place(0, deckY + 0.2, 1.38));
  mb.box(2.9, 0.95, 0.14, rail, place(0, deckY + 0.2, -1.38));
  mb.box(0.14, 0.95, 2.9, rail, place(1.38, deckY + 0.2, 0));
  mb.box(0.14, 0.95, 2.9, rail, place(-1.38, deckY + 0.2, 0));
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      mb.box(0.14, 2.0, 0.14, leg, place(sx * 1.3, deckY + 0.2, sz * 1.3));
    }
  }
  mb.cylinder(2.3, 0, 1.0, 4, p.color('roof.1'), place(0, deckY + 2.2, 0, Math.PI / 4));
  if (p.biome === 'arctic') mb.cylinder(1.5, 0, 0.66, 4, p.color('ground.3'), place(0, deckY + 2.58, 0, Math.PI / 4));
  // Searchlight housing on the south rail.
  mb.cylinder(0.32, 0.32, 0.5, 8, p.color('metal.mid'), place(0.7, deckY + 1.55, 1.2, 0, 1, Math.PI / 2), p.color('fire.3'));
  mb.box(0.1, 0.5, 0.1, p.color('metal.lo'), place(0.7, deckY + 1.1, 1.3));
  return mb;
};

/** The perimeter wall lives with its breach in walls.ts (ROUND7); re-exported for the look-dev scene. */
export { wallRun } from './walls';

/** Barbed wire along x: posts, three strands and tangles. Wounds on crossing; a grenade gaps it. */
export function wireRun(p: Palette, rng: Pcg32, length = 8): MeshBuilder {
  const mb = new MeshBuilder();
  const post = p.color('timber.lo');
  const wire = p.color('metal.lo');
  for (let x = -length / 2; x <= length / 2 + 0.01; x += 2) {
    mb.box(0.14, 1.35, 0.14, post, place(x, 0, 0, rng.range(-0.1, 0.1)));
  }
  for (const y of [0.35, 0.75, 1.15]) {
    mb.box(length, 0.07, 0.07, wire, place(0, y, 0));
  }
  // Diagonal tangles between posts so the run reads as wire rather than a rail fence.
  for (let x = -length / 2 + 1; x < length / 2; x += 2) {
    mb.box(0.07, 1.5, 0.07, wire, place(x, 0.1, 0.12, 0, 1, 0, 0.9));
    mb.box(0.07, 1.5, 0.07, wire, place(x, 0.1, -0.12, 0, 1, 0, -0.9));
  }
  return mb;
}

export const crate: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const s = rng.range(0.8, 1.05);
  mb.box(s, s * 0.8, s, { all: p.color('timber.mid'), top: capColor(p, 'timber.hi'), front: p.color('timber.mid') });
  mb.box(s + 0.04, 0.12, s + 0.04, p.color('timber.lo'), place(0, s * 0.34, 0));
  return mb;
};

export const barrel: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  const tone = rng.chance(0.3) ? 'enemyAccent.0' : 'metal.mid';
  mb.cylinder(0.36, 0.36, 0.95, 8, p.color(tone), undefined, capColor(p, 'metal.hi'));
  mb.cylinder(0.385, 0.385, 0.08, 8, p.color('metal.lo'), place(0, 0.3, 0));
  mb.cylinder(0.385, 0.385, 0.08, 8, p.color('metal.lo'), place(0, 0.62, 0));
  return mb;
};

/** Horizontal fuel tank on cradles: the classic thing to put a time bomb under. */
export const fuelTank: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.cylinder(1.15, 1.15, 4.4, 10, p.color('metal.mid'), place(-2.2, 1.45, 0, 0, 1, 0, -Math.PI / 2), p.color('metal.lo'));
  mb.box(0.5, 0.9, 2.0, p.color('concrete.lo'), place(-1.3, 0, 0));
  mb.box(0.5, 0.9, 2.0, p.color('concrete.lo'), place(1.3, 0, 0));
  mb.box(4.0, 0.16, 0.5, p.color('enemyAccent.0'), place(0, 2.52, 0));
  return mb;
};

/** Supply pod lying where it landed; the accent band marks it as ours and interactive. */
export const supplyPod: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.with(place(0, 0.4, 0, 0.5, 1, 0, -Math.PI / 2), () => {
    mb.cylinder(0.38, 0.38, 1.3, 8, p.color('canvas.lo'), place(0, -0.65, 0), p.color('canvas.lo'));
    mb.cylinder(0.4, 0.4, 0.22, 8, p.color('playerAccent.0'), place(0, -0.1, 0));
    mb.cylinder(0.38, 0.12, 0.4, 8, p.color('canvas.mid'), place(0, 0.65, 0));
  });
  return mb;
};

/** Collapsed parachute canopy: something the enemy can find (02 §3.3). */
export const canopy: KitBuilder = (p, rng) => {
  const mb = new MeshBuilder();
  mb.blob(2.2, 0.55, 1.7, p.color('paper.0'), place(0, 0.08, 0, rng.range(0, TAU)), {
    jitter: 0.3,
    rng,
    flatBottom: true,
  });
  return mb;
};

/** Radar antenna (mission 6 target): dish on a lattice mast. */
export const radar: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(1.6, 0.5, 1.6, { all: p.color('concrete.mid'), top: p.color('concrete.hi') });
  mb.cylinder(0.22, 0.16, 3.2, 6, p.color('metal.mid'), place(0, 0.5, 0));
  // Dish: a shallow cone tipped toward the south-west sky.
  mb.with(place(0, 3.9, 0, -0.6, 1, -1.0), () => {
    mb.cylinder(0.2, 1.7, 0.55, 10, p.color('metal.hi'), undefined, p.color('metal.lo'));
    mb.cylinder(0.05, 0.05, 1.1, 4, p.color('metal.lo'), place(0, 0.3, 0));
  });
  return mb;
};
